/**
 * Platform detection — single source of truth (Mar 2026).
 *
 * MESP officially ships to three runtime environments:
 *   • Android — the Capacitor WebView wrapper (`sa.mesp.app`).
 *   • PWA    — installed as a Progressive Web App via `display-mode: standalone`.
 *   • Web    — plain browser.
 *
 * This module exists so we NEVER sprinkle `window.Capacitor` /
 * `matchMedia('(display-mode: standalone)')` checks throughout the
 * codebase. Every UI visibility rule that varies per platform must
 * import from here.
 *
 * Public API:
 *   platform()      → 'android' | 'pwa' | 'web'
 *   isAndroidApp()  → boolean sugar for platform() === 'android'
 *   isPwaOrWeb()    → !isAndroidApp()  (browser-only UI predicate)
 *
 * Note: `isPwaOrWeb()` is TRUE for both plain browsers AND installed
 * PWAs. That is deliberate — the browser-only UI we gate (Install
 * prompt, Web-Push permission dialog) makes sense in both contexts.
 * If a future rule needs to distinguish `pwa` from `web`, use
 * `platform()` directly.
 */

const _hasWindow = () =>
  typeof window !== 'undefined' && window !== null;

/**
 * Returns `true` when the JS is running inside the Capacitor Android
 * shell. Capacitor injects `window.Capacitor` synchronously before
 * user code runs, and `getPlatform()` returns `"android"` on Android.
 * Web/PWA never sees `window.Capacitor.isNativePlatform() === true`.
 */
export const isAndroidApp = () => {
  if (!_hasWindow()) return false;
  const cap = window.Capacitor;
  if (!cap) return false;
  try {
    if (typeof cap.isNativePlatform === 'function' && !cap.isNativePlatform()) {
      return false;
    }
    const p = typeof cap.getPlatform === 'function' ? cap.getPlatform() : null;
    return p === 'android';
  } catch {
    return false;
  }
};

/**
 * `true` when the page is a browser tab OR an installed PWA in
 * standalone mode. The predicate any browser-only UI element should
 * be wrapped with.
 */
export const isPwaOrWeb = () => !isAndroidApp();

/**
 * `true` ONLY for a mobile BROWSER (phone/tablet web or PWA) — never
 * for the native Android app (which has its own NativeAudioMode
 * plugin) and never for desktop. Used to scope mobile-web-only audio
 * routing fixes (e.g. the mic-first warm-up in VoiceRoom).
 */
export const isMobileWebBrowser = () => {
  if (!_hasWindow()) return false;
  if (isAndroidApp()) return false;
  const ua = window.navigator?.userAgent || '';
  if (/Android|iPhone|iPod|Mobi/i.test(ua)) return true;
  // iPadOS 13+ masquerades as desktop Safari — detect via touch points.
  if (/iPad/i.test(ua)) return true;
  if (/Macintosh/i.test(ua) && (window.navigator?.maxTouchPoints || 0) > 1) return true;
  return false;
};

/**
 * Single string label for logs, telemetry, and downstream conditional
 * logic. NEVER used as a rendering guard directly — use `isAndroidApp`
 * / `isPwaOrWeb` for that so intent is obvious at the call site.
 */
export const platform = () => {
  if (isAndroidApp()) return 'android';
  if (!_hasWindow()) return 'web';
  try {
    if (window.matchMedia?.('(display-mode: standalone)').matches) return 'pwa';
    if (window.navigator?.standalone === true) return 'pwa';
  } catch {
    /* older browsers without matchMedia — fall through to 'web' */
  }
  return 'web';
};
